// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

import HubspotFormService from "utilities/services/hubspot/hubspot-form-service";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { ToastManager } from "utilities/toast/toast-manager";
import { HubspotFormPathParams } from "utilities/services/hubspot/hubspot-form-service";
import { t } from "i18next";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import HubspotFormFieldRecord from "models/view-models/hubspot/hubspot-form-field-record";
import HubspotFormSubmissionRecord from "models/view-models/hubspot/hubspot-form-submission-record";
import HubspotFormSubmissionFieldRecord from "models/view-models/hubspot/hubspot-form-submission-field-record";
import { SelectOption } from "../inputs/select/select";
import { HubspotFormSubmissionField } from "models/interfaces/hubspot/hubspot-form-submission";
import { HubspotFormField } from "models/interfaces/hubspot/hubspot-form-field";
import { StringUtils } from "utilities/string-utils";
import { CollectionUtils } from "utilities/collection-utils";

export interface UseHubspotFormProps {}

export interface UseHubspotStateProps {
    dirty: boolean;
    setDirty: Dispatch<SetStateAction<boolean>>;
    fields: HubspotFormFieldRecord[];
    fieldValueMap: Record<string, String>;
    isFormLoading: boolean;
    isFormSubmitting: boolean;
    onFieldChange: (fieldName: string, event: ChangePayload) => void;
    onSubmit: () => Promise<boolean>;
    resetFormState: () => void;
    formErrors: { [x: string]: string[] } | undefined;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

export type ChangePayload =
    | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    | SelectOption;

export const HubspotAutopopulatedFieldNames = {
    FirstName: "firstname",
    LastName: "lastname",
    Email: "email",
    PhoneNumber: "phone_number",
};
// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Hook
// -------------------------------------------------------------------------------------------------
export const useHubspotForm = (): UseHubspotStateProps => {
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [formSubmissionPayload, setFormSubmissionPayload] =
        useState<HubspotFormSubmissionRecord>();
    const [initialFormSubmissionPayload, setInitialFormSubmissionPayload] =
        useState<HubspotFormSubmissionRecord>();
    const [formErrors, setFormErrors] = useState<{ [x: string]: string[] } | undefined>();
    const [fields, setFields] = useState<HubspotFormFieldRecord[]>([]);
    const [dirty, setDirty] = useState<boolean>(false);
    const { record: globalState } = useGlobalState();
    const { get: getFormFieldsQuery } = HubspotFormService.useGetQuery();
    const { create: submitForm } = HubspotFormService.useCreate();
    const formId = globalState.azureSettings?.hubspotFormId || "";

    const autoPopulateFields = useCallback(
        (fields: HubspotFormSubmissionFieldRecord[]) => {
            // Record to hold changes
            const formFieldsWithUserInfo = fields.map((field) => {
                switch (field.name) {
                    case HubspotAutopopulatedFieldNames.FirstName:
                        return field.with({
                            value: globalState?.currentIdentity?.user?.firstName,
                        });
                    case HubspotAutopopulatedFieldNames.LastName:
                        return field.with({
                            value: globalState?.currentIdentity?.user?.lastName,
                        });
                    case HubspotAutopopulatedFieldNames.Email:
                        return field.with({
                            value: globalState?.currentIdentity?.user?.email,
                        });
                    case HubspotAutopopulatedFieldNames.PhoneNumber:
                        return field.with({
                            value: globalState?.currentIdentity?.user?.phoneNumber,
                        });
                    default:
                        return field;
                }
            });

            return formFieldsWithUserInfo;
        },
        [
            globalState?.currentIdentity?.user?.email,
            globalState?.currentIdentity?.user?.firstName,
            globalState?.currentIdentity?.user?.lastName,
            globalState?.currentIdentity?.user?.phoneNumber,
        ]
    );

    useEffect(() => {
        const fetchFormFields = async () => {
            if (formId === "") return;
            try {
                const hubspotFormPathParams: HubspotFormPathParams = {
                    formId,
                };
                const formFieldsResult = await getFormFieldsQuery(hubspotFormPathParams);
                if (formFieldsResult.result?.hasErrors() || formFieldsResult.resultObject == null) {
                    return;
                }
                setFields(formFieldsResult.resultObject?.fields);
                if (!formSubmissionPayload?.portalId) {
                    let fieldsToUpdate = initiateSubmissionRecord(
                        formFieldsResult?.resultObject?.fields ?? []
                    );
                    if (globalState.isAuthenticated()) {
                        fieldsToUpdate = autoPopulateFields(fieldsToUpdate);
                    }
                    const submission = new HubspotFormSubmissionRecord().with({
                        portalId: formFieldsResult.resultObject?.portalId,
                        fields: [...fieldsToUpdate],
                    });
                    setFormSubmissionPayload(submission);
                    setInitialFormSubmissionPayload(submission);
                }
            } catch {
                ToastManager.error("error"); // TODO: Needs error string
            }
        };
        fetchFormFields();
    }, [
        autoPopulateFields,
        formId,
        formSubmissionPayload?.portalId,
        getFormFieldsQuery,
        globalState,
    ]);

    const fieldValueMap: Record<string, String> =
        formSubmissionPayload?.fields.reduce(
            (obj, field) => ({ ...obj, [field.name]: field.value ?? "" }),
            {} as Record<string, string>
        ) ?? {};

    const handleSubmissionChange = (fieldName: string, value: string) => {
        setFormSubmissionPayload((prev) =>
            formSubmissionPayload?.with({
                fields:
                    prev?.fields.map((field) =>
                        field.name === fieldName ? field.with({ value }) : field
                    ) ?? [],
            })
        );
    };
    const onFieldChange = (fieldName: string, event: ChangePayload) => {
        if (!dirty) setDirty(true);
        setFormErrors((prev) => ({ ...prev, [fieldName]: [] }));
        if ("target" in event) {
            handleSubmissionChange(fieldName, event.target.value);
        } else {
            handleSubmissionChange(fieldName, event.value);
        }
    };

    const resetFormState = () => {
        setFormSubmissionPayload(initialFormSubmissionPayload);
        setFormErrors({});
        setDirty(false);
    };

    const onSubmit = async (): Promise<boolean> => {
        setIsFormSubmitting(true);
        try {
            const { isErrored, formErrors } = validateForm();
            if (isErrored) {
                setFormErrors(formErrors);
                return false;
            }
            const response = await submitForm(formSubmissionPayload!, {
                formId,
                portalId: formSubmissionPayload?.portalId!,
            });
            if (!response) {
                throw new Error();
            }
            setIsFormSubmitting(false);
            return true;
        } catch (error) {
            ToastManager.error(t("thereWasAnErrorSubmittingYourQuestion") as string);
            setIsFormSubmitting(false);
            return false;
        }
    };

    const validateField = (
        submissionField: HubspotFormSubmissionField,
        sourceField: HubspotFormField
    ) => {
        const errors: string[] = [];
        if (sourceField.required && !submissionField.value) {
            errors.push(t("required", { name: sourceField.label }));
        } else if (
            sourceField.name === HubspotAutopopulatedFieldNames.Email &&
            !StringUtils.isValidEmail(submissionField.value)
        ) {
            errors.push(t("pleaseEnterAValidEmailAddress"));
        }
        // TODO: may need min/max length validation
        return errors;
    };

    const validateForm = () => {
        let isErrored = false;
        const formErrors = formSubmissionPayload?.fields
            ?.map((submissionField) => {
                const matchingSourceField = fields.find(
                    (sourceField) => sourceField.name === submissionField.name
                );
                const errors = validateField(submissionField, matchingSourceField!);
                if (CollectionUtils.isNotEmpty(errors)) {
                    isErrored = true;
                }
                return {
                    name: submissionField.name,
                    errors: errors,
                };
            })
            .reduce((acc, field) => {
                acc[field.name] = field.errors;
                return acc;
            }, {} as Record<string, any>);

        return { isErrored, formErrors };
    };

    const isFormLoading = false;

    return {
        dirty,
        setDirty,
        fields,
        fieldValueMap,
        isFormLoading,
        isFormSubmitting,
        onFieldChange,
        onSubmit,
        resetFormState,
        formErrors,
    };
};
// #endregion Hook

const initiateSubmissionRecord = (
    fields: HubspotFormFieldRecord[]
): HubspotFormSubmissionFieldRecord[] =>
    fields.map((field) => {
        return new HubspotFormSubmissionFieldRecord().with({
            value: "",
            name: field.name,
            objectTypeId: field.objectTypeId,
        });
    });
